import isPlainObject from 'lodash/isPlainObject';
import {i18nString} from 'typesafe-i18n';
import intlLocale from '@utils/intlLocale';
import deepFindInObject from '../../../../../api/tinyUtils/deepFindInObject';

/**
 * @typedef {import('typesafe-i18n').TranslateByString} TranslateByString
 */

const {labelId} = globalThis.globalParams,
      // selects every dynamic value from the backend translations including the '%' signs
      BACKEND_DYNAMIC_VALUES_REGEX = /%(\b\w+)%/gu;

class TranslatorService {
	constructor() {
		/**
		 * @type {Record<string, Record<string, unknown>>}
		 * @private
		 */
		this.translations = this.getPageParsedTranslations();
		/**
		 * @type {TranslateByString}
		 * @private
		 */
		this.i18nString = i18nString(intlLocale());
	}
	
	/**
	 * @param {Record<string, Record<string, unknown>>} rawTranslations
	 * @returns {Record<string, Object>}
	 * @private
	 */
	getParsedTranslations(rawTranslations) {
		const parsed = Object.create(null);
		
		for (const [key, trans] of Object.entries(rawTranslations)) {
			if (isPlainObject(trans)) {
				parsed[key] = this.getParsedTranslations(trans);
			} else if (typeof trans === 'string') {
				parsed[key] = trans.replaceAll(BACKEND_DYNAMIC_VALUES_REGEX, '{$1}'); // turns %name% => {name}
			} else {
				parsed[key] = trans;
			}
		}
		
		return parsed;
	}
	
	/**
	 * @returns {Record<string, Object>}
	 * @private
	 */
	fetchTranslations() {
		let translations = {};
		
		for (const translationsTemplate of document.querySelectorAll('.frontendTranslationsTemplate')) {
			translations = {
				...translations,
				...JSON.parse(translationsTemplate.innerHTML)
			};
		}
		
		return translations;
	}
	
	/**
	 * Returns the translations found on the current page
	 * @returns {Record<string, Record<string, unknown>>}
	 * @private
	 */
	getPageParsedTranslations() {
		const rawTranslations = this.fetchTranslations();
		
		return this.getParsedTranslations(rawTranslations);
	}
	
	/**
	 * Checks availability using the same label fallback as trans(), without logging or interpolation.
	 * @param {string} key
	 * @param {{ns: string}} internalParams
	 * @returns {boolean}
	 */
	existingTrans(key, {ns}) {
		return Boolean(this._getTranslation(key, ns));
	}

	/**
	 * @param {string} key
	 * @param {string} ns
	 * @returns {unknown}
	 * @private
	 */
	_getTranslation(key, ns) {
		return deepFindInObject(`${ns}.${labelId}_${key}`, this.translations) ??
			deepFindInObject(`${ns}.${key}`, this.translations);
	}

	/**
	 * @param {string} key
	 * @param {{
	 * ns: string
	 * }} internalParams
	 * @param {Object} [params]
	 * @returns {unknown}
	 */
	trans(key, {ns}, params) {
		const translation = this._getTranslation(key, ns);

		if (!translation) {
			console.error(`Translation for key: ${key} not found`);

			return '';
		}

		if (typeof translation !== 'string') {
			return translation;
		}

		return this.i18nString(translation, params);
	}
}

const translatorService = new TranslatorService(),
      trans = translatorService.trans.bind(translatorService),
      existingTrans = translatorService.existingTrans.bind(translatorService);

export default translatorService;
export {trans, existingTrans};